/**
 * Cell-formatting helpers for the Case Appeals table.
 */

import { TableCellWithBadge } from "../../../../data-model/tableCells";
import { CmmStpStatus } from "../../../../enums/cmmStpStatus";
import {
  LEGACY_APPEAL_SECOND_STP_STATUS,
  SecondStpStatus,
  SecondStpStatusLabels,
} from "../../../../enums/secondStpStatus";
import {
  getBgColorForStpStatus,
  getTextColorForStpStatus,
} from "../../../pharmaStpFile/table/utils/getStpStatusColors";

/**
 * Normalizes a CoverMyMeds ID from an appeal list row for table display.
 *
 * @param value - Raw `covermymed_id` from the API
 * @returns Display string, or empty when missing/invalid
 */
export const formatCoverMyMedsId = (
  value: string | null | undefined,
): string => {
  if (!value || value === "nan" || value === "NaN") {
    return "";
  }
  return String(value);
};

/**
 * Builds a Second STP Status badge for a Case Appeals table cell.
 *
 * @param status - Raw `second_stp_status` from the API
 * @returns Badge cell with label and colors
 */
export const getSecondStpStatusBadge = (
  status: string | null | undefined,
): TableCellWithBadge => {
  if (!status) {
    return {
      text: "Not Sent",
      color: "#111111",
      bgColor: "#F5F5F5",
    };
  }

  const trimmed = status.trim();
  // Rows saved before the rename hold "Appeals"; lowercase "appeal(s)" writes
  // predate that. All of them display as the current "Appeal" label.
  const lowered = trimmed.toLowerCase();
  const isAppealStatus =
    lowered === SecondStpStatus.APPEAL.toLowerCase() ||
    lowered === LEGACY_APPEAL_SECOND_STP_STATUS.toLowerCase();
  const normalizedStatus = isAppealStatus ? SecondStpStatus.APPEAL : trimmed;

  return {
    text:
      SecondStpStatusLabels[normalizedStatus as SecondStpStatus] ??
      normalizedStatus,
    color: getTextColorForStpStatus(normalizedStatus as CmmStpStatus) ?? "#111111",
    bgColor: getBgColorForStpStatus(normalizedStatus as CmmStpStatus) ?? "#F5F5F5",
  };
};

/**
 * Builds an Appeal Probability badge for a Case Appeals table cell.
 * Always uses black text on a grey badge.
 *
 * @param probability - Numeric probability from the API (e.g. 65.0)
 * @param band - Optional probability band used for hover text only
 * @returns Badge cell with percent label
 */
export const getAppealProbabilityBadge = (
  probability: number | null | undefined,
  band?: string | null,
): TableCellWithBadge => {
  if (probability == null || Number.isNaN(probability)) {
    return {
      text: "--",
      color: "#0F0F0F",
      bgColor: "#F5F5F5",
      hoverText: "--",
    };
  }

  const text = `${probability}%`;
  return {
    text,
    color: "#0F0F0F",
    bgColor: "#F5F5F5",
    hoverText: band?.trim() ? `${text} (${band.trim()})` : text,
  };
};
